import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as api from '../api/client';
import { BUDGET_CHANGED } from '../utils/budgetEvents';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import CopilotSidebar from './CopilotSidebar';
import NotificationBell from './NotificationBell';
import { useBrand } from '../context/BrandContext';
import lightLogo from '../assets/B-Agent_Logo_Light.svg';
import darkLogo from '../assets/B-Agent_Logo.svg';

const SIDEBAR_KEY = 'bagent-sidebar-open';
/** Below this the sidebar overlays the page instead of sitting beside it. */
const OVERLAY_MAX_WIDTH = 1000;

function loadSidebarOpen() {
  try {
    const saved = localStorage.getItem(SIDEBAR_KEY);
    if (saved !== null) return saved !== '0';
  } catch { /* ignore */ }
  // Default open on a desktop, closed on a phone where it would cover the page.
  return typeof window === 'undefined' || window.innerWidth > OVERLAY_MAX_WIDTH;
}

const isOverlay = () => typeof window !== 'undefined' && window.innerWidth <= OVERLAY_MAX_WIDTH;

/**
 * The header shows the allowance in units of 100 tokens, so a $20 budget reads 50.0K rather
 * than 5M.
 *
 * The /100 scale is what was asked for, twice and consistently ("1M ... show it as 10k",
 * then "if its 5M ... show it as 50k"). The DECIMAL is what makes it a working counter: at
 * "40k" a whole tenth of the budget had to go before the digit changed.
 *
 * Measured, not assumed. One decimal step is 100 units = 0,20% of the budget = $0,04:
 *     short chat turn, gpt-4.1        12 turns
 *     normal chat turn, terra          4 turns
 *     document analysis                1 turn
 *     document generation              1 turn
 * So it moves within a few turns of real work, and every single time a document is analysed
 * or generated - which is where the budget actually goes. A first attempt at this comment
 * claimed "every five or six turns" flatly; testing with short gpt-4.1 greetings showed no
 * movement across six, because that is the cheapest thing the app can do and the least
 * representative. Per-turn animation is not achievable at monthly-budget scale and is not
 * what a budget indicator is for.
 *
 * A dot decimal and a capital K, matching how the format was asked for. That is
 * English-style rather than the tr-TR comma used elsewhere, but "40.4K" is read as one
 * abbreviation rather than as a number plus a separator, so it does not collide with the
 * Turkish convention. The decimal is always shown, including "50.0K", so the string keeps a
 * stable width and the header does not shift as the number falls.
 *
 * The tooltip always carries the real token count and the real dollars. The scaled figure is
 * a reading aid and must never be the only number on offer.
 */
const TOKENS_PER_UNIT = 100;

function compactTokens(n) {
  if (!Number.isFinite(n)) return '';
  const units = Math.round(n / TOKENS_PER_UNIT);
  if (units >= 1_000_000) return `${(units / 1_000_000).toFixed(1)}M`;
  if (units >= 1_000) return `${(units / 1_000).toFixed(1)}K`;
  return String(units);
}

/**
 * Remaining tokens, in the header.
 *
 * Polling would be wrong: the only thing that moves this is a model call the user just
 * made, so pages fire BUDGET_CHANGED when one completes and the counter refetches. A user
 * with no enforced limit sees nothing at all rather than a meaningless number.
 */
function TokenMeter({ t }) {
  const { user } = useAuth();
  const [budget, setBudget] = useState(null);

  useEffect(() => {
    let live = true;
    const load = () => api.getMyBudget(user?.role)
      .then((b) => { if (live) setBudget(b); })
      .catch(() => { /* the counter is informational; never block the header on it */ });
    load();
    window.addEventListener(BUDGET_CHANGED, load);
    return () => { live = false; window.removeEventListener(BUDGET_CHANGED, load); };
  }, [user?.role]);

  if (!budget?.enforced || budget.tokensRemaining == null) return null;
  const pct = budget.percentUsed;
  return (
    <span
      className={`token-meter${pct >= 90 ? ' critical' : pct >= 75 ? ' warn' : ''}`}
      title={t('budget.tooltip')
        .replace('{used}', budget.usedUsd.toFixed(2))
        .replace('{limit}', budget.limitUsd.toFixed(2))
        .replace('{tokens}', budget.totalTokens.toLocaleString('tr-TR'))}
    >
      {compactTokens(budget.tokensRemaining)} {t('budget.tokenUnit')}
    </span>
  );
}



export default function Layout() {
  const { logout, idleSecondsLeft, stayActive } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const { settings } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(loadSidebarOpen);
  const customLogo = settings[theme]?.logoUrl;
  const logoSrc = customLogo || (theme === 'dark' ? darkLogo : lightLogo);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, sidebarOpen ? '1' : '0'); } catch { /* ignore */ }
  }, [sidebarOpen]);

  // Only auto-close on navigation while overlaying, otherwise every click would collapse
  // the sidebar the user deliberately left open.
  useEffect(() => {
    if (isOverlay()) setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSidebarNavigate = () => {
    if (isOverlay()) setSidebarOpen(false);
  };

  return (
    <div className="layout">
      {/* Idle timeout is about to sign the user out — say so before it happens. */}
      {typeof idleSecondsLeft === 'number' && (
        <div className="session-warning" role="alert">
          <span>{t('auth.idleWarning', { seconds: idleSecondsLeft })}</span>
          <button type="button" className="btn btn-primary btn-sm" onClick={stayActive}>
            {t('auth.staySignedIn')}
          </button>
        </div>
      )}
      <header className="app-header">
        <div className="app-header-inner">
          {/* The logo IS the menu control - one mark doing the job the burger did, which
              is how most product shells behave. It stays a <button>, not a link: it toggles
              the sidebar, and a screen reader must be told that rather than being told it
              navigates. The wordmark beside it is a separate link home, so "go to the
              dashboard" is still reachable and does not collide with "open the menu". */}
          <button
            type="button"
            className="header-logo-btn"
            aria-expanded={sidebarOpen}
            aria-controls="app-sidebar"
            aria-label={sidebarOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            title={sidebarOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            onClick={() => setSidebarOpen((o) => !o)}
          >
            <img src={logoSrc} alt="" className="app-logo-img" aria-hidden />
          </button>
          <Link to="/" className="app-logo" aria-label={t('appName')}>
            <span className="app-logo-brand">
              <span className="app-logo-brand-primary">B-Agent</span>{' '}
              <span className="app-logo-brand-law">Law</span>
            </span>
          </Link>
          <div className="user-bar">
            <div className="header-actions">
              <TokenMeter t={t} />
              <NotificationBell />
              {/* One select instead of two competing buttons. Two of them meant the
                  inactive language always looked like an unpressed control, and the pair
                  took the width of a third icon for a setting changed once. */}
              <select
                className="lang-select"
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                aria-label="Language"
                title="Language"
              >
                <option value="tr">TR</option>
                <option value="en">EN</option>
              </select>
              <button
                type="button"
                className="theme-btn"
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <svg className="theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="12" r="5" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                ) : (
                  <svg className="theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Navigation lives in the sidebar now; the header keeps only brand and account. */}
      <div className={`layout-body${sidebarOpen ? ' sidebar-open' : ''}`}>
        <div id="app-sidebar" className="sidebar-slot" aria-hidden={!sidebarOpen}>
          {sidebarOpen && <CopilotSidebar onNavigate={handleSidebarNavigate} />}
        </div>
        {sidebarOpen && (
          <button
            type="button"
            className="sidebar-backdrop"
            aria-label={t('nav.closeMenu')}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
